import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Download,
  SlidersHorizontal,
  Copy,
} from "lucide-react";
import { listDeliveryReportsDetailed, type CurrentUser, type DeliveryReportItem } from "../../lib/api";

export function DeliveryReportsDetailedView({}: { token: string; user: CurrentUser }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [instanceFilter, setInstanceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const reportsQuery = useQuery({
    queryKey: ["delivery-reports-detailed"],
    queryFn: () => listDeliveryReportsDetailed(),
    refetchInterval: 10_000,
  });

  const reportItems = reportsQuery.data || [];

  const filteredReports = reportItems.filter((item) => {
    const matchesSearch =
      item.receiver.includes(searchQuery) ||
      item.message_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.user_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesInstance = instanceFilter === "all" || item.instance_name === instanceFilter;
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesInstance && matchesStatus;
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  function exportCsv() {
    const headers = ["Message ID,User,Instance,Receiver,Type,Content,Status,Submitted,Sent,Delivered,Read,ErrorCode"];
    const rows = filteredReports.map((r) =>
      `"${r.message_id}","${r.user_name}","${r.instance_name}","${r.receiver}","${r.type}","${r.content.replace(/"/g, '""')}","${r.status}","${r.submitted_at}","${r.sent_at}","${r.delivered_at || ""}","${r.read_at || ""}","${r.error_code || ""}"`
    );
    const csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `delivery_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <div className="space-y-5">
      {/* Header (Matches 04_delivery_report.png) */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Delivery Report</h1>
        <p className="text-xs text-slate-500 mt-1">
          Monitor and analyze message delivery status from your organizations.
        </p>
      </div>

      {/* Filter Bar (Matches 04_delivery_report.png Filter Toolbar) */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative w-64">
            <input
              type="text"
              placeholder="Search messages..."
              className="modern-input w-full text-xs py-2"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Dotted Filter Chips: ⊕ Instance, ⊕ Status, ⊕ Date range */}
          <select
            value={instanceFilter}
            onChange={(e) => setInstanceFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ Instance</option>
            <option value="VIP Support QR">VIP Support QR</option>
            <option value="Marketing Node 1">Marketing Node 1</option>
            <option value="Transactional QR">Transactional QR</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-chip cursor-pointer outline-none"
          >
            <option value="all">⊕ Status</option>
            <option value="read">Read</option>
            <option value="delivered">Delivered</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>

          <button className="filter-chip">
            <span>⊕ Date range</span>
          </button>
        </div>

        {/* Action Buttons: Export Report & View */}
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="btn-secondary text-xs py-2 px-3.5 flex items-center gap-1.5 shadow-2xs">
            <Download className="w-3.5 h-3.5 text-slate-500" />
            <span>Export Report</span>
          </button>

          <button className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 shadow-2xs">
            <SlidersHorizontal className="w-3.5 h-3.5 text-slate-500" />
            <span>View</span>
          </button>
        </div>
      </div>

      {/* Delivery Report Table (Matches 04_delivery_report.png layout) */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Message ID</th>
                <th>User</th>
                <th>Instance</th>
                <th>Receiver</th>
                <th>Type</th>
                <th>Content</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Sent/Failed At</th>
                <th>Delivered/Read At</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((report: DeliveryReportItem) => (
                <tr key={report.id} className="hover:bg-slate-50/70 transition-colors">
                  {/* Message ID with Copy */}
                  <td>
                    <div className="flex items-center gap-1 font-mono text-xs text-slate-700">
                      <button onClick={() => copyText(report.message_id)} className="hover:text-slate-900">
                        <Copy className="w-3 h-3 text-slate-400" />
                      </button>
                      <span title={report.message_id}>{report.message_id.slice(0, 10)}...</span>
                    </div>
                  </td>

                  {/* User */}
                  <td className="text-xs font-semibold text-slate-800">{report.user_name}</td>

                  {/* Instance */}
                  <td className="text-xs font-medium text-slate-700">{report.instance_name}</td>

                  {/* Receiver */}
                  <td className="font-mono text-xs text-slate-900 font-bold">{report.receiver}</td>

                  {/* Type */}
                  <td className="capitalize text-xs font-mono text-slate-500">{report.type}</td>

                  {/* Content */}
                  <td className="text-xs text-slate-600 max-w-xs truncate" title={report.content}>
                    {report.content}
                  </td>

                  {/* Status */}
                  <td>
                    <span
                      className={`status-pill ${
                        report.status === "read"
                          ? "bg-sky-50 text-sky-700 border border-sky-200"
                          : report.status === "delivered"
                          ? "status-pill--connected"
                          : report.status === "sent"
                          ? "bg-slate-100 text-slate-700 border border-slate-200"
                          : "status-pill--disconnected"
                      }`}
                    >
                      {report.status}
                    </span>
                  </td>

                  {/* Submitted */}
                  <td className="font-mono text-[11px] text-slate-500">{report.submitted_at.slice(11)}</td>

                  {/* Sent / Failed At */}
                  <td className="font-mono text-[11px] text-slate-500">
                    {report.sent_at.slice(11)}
                    {report.error_code && (
                      <div className="text-[10px] text-rose-600 font-sans font-bold">{report.error_code}</div>
                    )}
                  </td>

                  {/* Delivered / Read At */}
                  <td className="font-mono text-[11px] text-slate-500">
                    {report.read_at ? (
                      <span className="text-sky-600 font-medium">{report.read_at.slice(11)} (Read)</span>
                    ) : report.delivered_at ? (
                      <span className="text-emerald-600 font-medium">{report.delivered_at.slice(11)}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
